import { useEffect } from "react";

/**
 * Popover boven de Composer met de vier "iets aan het bericht
 * toevoegen"-acties — camera, bestand, poll, locatie. Zelfde
 * positionering als EmojiPicker (absolute, sluit bij buiten-klik).
 * Acties zijn contextueel: caller filtert ze al via z'n props.
 *
 * Bewust GEEN full-screen bottom-sheet: een lokaal popovertje boven
 * de Composer voelt sneller en is consistent met de bestaande
 * EmojiPicker. Animatie blijft sober — slide-in 'n paar pixels.
 */

export type AttachmentAction = {
  key: "camera" | "file" | "poll" | "location";
  label: string;
  icon: JSX.Element;
  onPick(): void;
  available: boolean;
};

export default function AttachmentSheet({
  actions,
  onClose,
}: {
  actions: AttachmentAction[];
  onClose(): void;
}) {
  // Escape sluit 'm; klik buiten ook (via de invisible overlay).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const visible = actions.filter((a) => a.available);
  if (visible.length === 0) return null;

  return (
    <>
      {/* Onzichtbare overlay zodat klikken buiten de sheet 'm sluit,
          zonder dat de rest van de UI er stuk van gaat. Pointer-events
          alleen op de overlay, niet op de items zelf. */}
      <div
        className="fixed inset-0 z-30"
        onPointerDown={onClose}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-full left-1 right-1 z-40 mb-1 overflow-hidden rounded-xl border border-slate-700 bg-slate-800 shadow-lg"
        style={{ animation: "swift-pop 120ms ease-out" }}
      >
        <ul className="divide-y divide-slate-700">
          {visible.map((a) => (
            <li key={a.key}>
              <button
                type="button"
                onClick={() => {
                  a.onPick();
                  onClose();
                }}
                className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-slate-100 hover:bg-slate-700"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center text-slate-300">
                  {a.icon}
                </span>
                <span>{a.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
